import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useAsyncEffect from 'use-async-effect';
import { DeviceSpecificLayout } from 'components/Layout/DeviceSpecificLayout';
import { Loader } from 'components/Loader/Loader';
import { UserContext } from 'context/userContext';
import { useBreakpoint } from 'hooks/useBreakpoint';
import { usePlayerLayout } from 'lib/playerLayout';
import { lucky6Lang } from 'services/api/lucky6Service';
import { setLucky6Frame, setLucky6Slip } from 'lib/lucky6Bridge';
import styles from '../styles/pages/LuckySix.module.css';

type Props = {
  // Opens a session on the provider and returns the URL for the iframe.
  launch: (lang: string, channel: 'web' | 'mobile') => Promise<string>;
  title: string; // iframe title
  labelKey: string; // title band (classic layout)
  unavailableKey: string;
  // The game reports its ticket to our modern bottom bar (Lucky Six).
  hostedSlip?: boolean;
};

// A game from one of our providers (Lucky Six, Keno), in an iframe. Stakes
// and wins go through our wallet, so the balance in the header follows.
export const ProviderGame = ({
  launch,
  title,
  labelKey,
  unavailableKey,
  hostedSlip,
}: Props) => {
  const { t, i18n } = useTranslation();
  const { loggedUser } = useContext(UserContext);
  const { isMobile } = useBreakpoint();
  const layout = usePlayerLayout();
  const modern = layout === 'modern';
  const [url, setUrl] = useState('');
  const [failed, setFailed] = useState(false);
  const frame = useRef<HTMLIFrameElement>(null);
  const [mobileHeight, setMobileHeight] = useState<number | null>(null);

  // On mobile the game has its own bar with the stake and the place button at
  // the bottom of its window, so the iframe must end exactly where the page's
  // fixed bottom navigation starts; otherwise that bar is hidden under it.
  const fit = useCallback(() => {
    const el = frame.current;
    if (!el || !isMobile) return;
    const rect = el.getBoundingClientRect();
    const top = rect.top + window.scrollY;
    // Only fixed or sticky bars (tab bar, ticket bars) count; measuring the
    // page itself would keep whatever gap the last height left.
    const x = window.innerWidth / 2;
    const barOf = (n: Element | null): Element | null => {
      for (let e = n; e && e !== document.body; e = e.parentElement) {
        if (e === el) return null;
        const pos = getComputedStyle(e).position;
        if (pos === 'fixed' || pos === 'sticky') return e;
      }
      return null;
    };
    let inset = 0;
    for (let y = window.innerHeight - 1; y > rect.top; y -= 4) {
      const bar = barOf(document.elementFromPoint(x, y));
      if (!bar) break;
      // A bar's full height: one already pushed below the screen (by a
      // game taller than it should be) shows only its top.
      const b = bar.getBoundingClientRect();
      inset = Math.max(inset, window.innerHeight - b.top, b.height);
      y = Math.min(y, b.top);
    }
    setMobileHeight(
      Math.max(420, Math.floor(window.innerHeight - top - inset))
    );
  }, [isMobile]);

  // Opened from further down the home page, the page would keep that
  // scroll and show the game cut in half: always start at the top.
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // On a phone the game fills the screen down to the bottom bar and scrolls
  // inside itself. The page around it must not scroll: a swipe that reaches
  // the end of the game's list would otherwise move the page and leave an
  // empty band above the bottom bar.
  useEffect(() => {
    if (!isMobile) return;
    // Only the root: it locks the window. Locked too, the body became a
    // scroller of its own, and the bottom menu (sticky to the window) sat
    // under the page instead of on the screen's bottom edge.
    const html = document.documentElement;
    const prev = html.style.overflow;
    html.style.overflow = 'hidden';
    window.scrollTo(0, 0);
    return () => {
      html.style.overflow = prev;
    };
  }, [isMobile]);

  // The game reports its ticket for the bottom bar.
  useEffect(() => {
    if (!url || !hostedSlip) return;
    const origin = new URL(url).origin;
    const win = () => frame.current?.contentWindow ?? null;
    setLucky6Frame(win());
    const on = (e: MessageEvent) => {
      if (
        e.origin !== origin ||
        e.source !== win() ||
        e.data?.type !== 'lucky6:slip'
      )
        return;
      const d = e.data;
      setLucky6Slip({
        count: Number(d.count) || 0,
        stake: Number(d.stake) || 0,
        maxWin: Number(d.maxWin) || 0,
        currency: String(d.currency ?? ''),
      });
      setTimeout(fit, 50);
    };
    window.addEventListener('message', on);
    return () => {
      window.removeEventListener('message', on);
      setLucky6Frame(null);
    };
  }, [url, fit, hostedSlip]);

  useEffect(() => {
    if (!url) return;
    fit();
    const t = setTimeout(fit, 400);
    window.addEventListener('resize', fit);
    // Ticket bars come and go (e.g. the sports ticket is emptied): refit
    // whenever the page outside the game changes, at most once a frame.
    let raf = 0;
    const mo = new MutationObserver(() => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(fit);
    });
    mo.observe(document.body, { childList: true, subtree: true });
    return () => {
      clearTimeout(t);
      cancelAnimationFrame(raf);
      mo.disconnect();
      window.removeEventListener('resize', fit);
    };
  }, [url, fit]);

  useAsyncEffect(
    async (isMounted) => {
      setUrl('');
      setFailed(false);
      if (!loggedUser) return;
      try {
        const next = await launch(
          lucky6Lang(i18n.language),
          isMobile ? 'mobile' : 'web'
        );
        // Modern layout on a phone: our bottom bar shows the Lucky Six
        // ticket (host=1); elsewhere the game keeps its own bar.
        const hosted = !!hostedSlip && modern && isMobile;
        if (isMounted())
          setUrl(
            hosted ? next + (next.includes('?') ? '&' : '?') + 'host=1' : next
          );
      } catch {
        if (isMounted()) setFailed(true);
      }
    },
    [loggedUser?.id, i18n.language, isMobile, modern, launch, hostedSlip]
  );

  let body;
  if (!loggedUser)
    body = <p className={styles.message}>{t('must_be_logged_in')}</p>;
  else if (failed) body = <p className={styles.message}>{t(unavailableKey)}</p>;
  else if (!url) body = <Loader />;
  else
    body = (
      <iframe
        ref={frame}
        onLoad={() =>
          hostedSlip && setLucky6Frame(frame.current?.contentWindow ?? null)
        }
        className={isMobile ? styles.frameMobile : styles.frame}
        // The measured height wins over the CSS minimum (560px), which on a
        // short phone pushed the bottom menu below the screen.
        style={
          isMobile && mobileHeight
            ? { height: mobileHeight, minHeight: 0 }
            : undefined
        }
        src={url}
        title={title}
        allow="autoplay; fullscreen"
      />
    );

  return (
    <DeviceSpecificLayout
      // Classic only: modern and VIP name the page in their header.
      mobileLayoutProps={{
        title: layout === 'classic' ? t(labelKey) : undefined,
      }}
    >
      <div className={styles.wrapper}>{body}</div>
    </DeviceSpecificLayout>
  );
};
